import { ReactRenderer } from "@tiptap/react";
import {
  computePosition,
  flip,
  shift,
  autoUpdate,
  offset,
} from "@floating-ui/dom";

import MentionList from "./commands-list.js";
import { type Editor } from "@tiptap/core";

export default ({
  items,
  chars,
  startOfLine = true,
  oneCharTrigger = true,
  breakRegexWords = [],
  allowedPrefixes,
}: {
  items: (props: { query: string; editor: Editor }) => any[] | Promise<any[]>;
  chars: string[];
  startOfLine?: boolean;
  oneCharTrigger?: boolean;
  breakRegexWords?: string[];
  allowedPrefixes?: string[] | null;
}) => ({
  breakRegexWords,
  items,
  ...(allowedPrefixes !== undefined && {
    allowedPrefixes,
  }),
  chars,
  startOfLine,
  oneCharTrigger,
  render: () => {
    let component;
    let floatingElement;
    let virtualElement;
    let cleanup;

    return {
      onStart: (props) => {
        component = new ReactRenderer(MentionList, {
          props,
          editor: props.editor,
        });

        if (!props.clientRect) {
          return;
        }

        // Create floating element
        floatingElement = document.createElement("div");
        floatingElement.style.position = "absolute";
        floatingElement.style.top = "0";
        floatingElement.style.left = "0";
        floatingElement.style.zIndex = "9999";
        floatingElement.appendChild(component.element);
        document.body.appendChild(floatingElement);

        // Create virtual element for positioning
        virtualElement = {
          getBoundingClientRect: props.clientRect,
        };

        // Compute initial position and set up auto-update
        const updatePosition = () => {
          computePosition(virtualElement, floatingElement, {
            placement: "bottom-start",
            middleware: [
              offset(5),
              flip({
                fallbackPlacements: ["top-start", "bottom-end", "top-end"],
              }),
              shift({ padding: 8 }),
            ],
          }).then(({ x, y }) => {
            Object.assign(floatingElement.style, {
              left: `${x}px`,
              top: `${y}px`,
            });
          });
        };

        updatePosition();
        cleanup = autoUpdate(virtualElement, floatingElement, updatePosition);
      },

      onUpdate(props) {
        if (!component || !floatingElement || !virtualElement) {
          return;
        }

        component.updateProps(props);

        if (!props.clientRect) {
          return;
        }

        // Update virtual element's bounding rect
        virtualElement.getBoundingClientRect = props.clientRect;
      },

      onKeyDown(props) {
        if (props.event.key === "Escape") {
          if (floatingElement) {
            floatingElement.style.display = "none";
          }
          return true;
        }

        return component.ref?.onKeyDown(props);
      },

      onExit() {
        // Clean up auto-update
        if (cleanup) {
          cleanup();
        }

        // Remove floating element from DOM
        if (floatingElement && floatingElement.parentNode) {
          floatingElement.parentNode.removeChild(floatingElement);
        }

        // Destroy component
        if (component) {
          component.destroy();
        }
      },
    };
  },
});
